// G11 — 52 Questions for Your Strategy Offsite + P05/P06/P07 pricing tools.
import { useMemo, useState } from "react";
import { Note, ToolSection, Field, useNumbers, ResultRow } from "./ui";
import { pct, inr } from "./engines";

const SUITS = {
  Market: [
    "Which assumption in our current plan is oldest — and when did we last test it against the market?",
    "If our biggest channel stopped working next quarter, what revenue survives, and who owns that answer?",
    "What is the market growing into that we are not organised to capture?",
    "Which competitor move in the last 12 months did we notice late — and why did we notice late?",
    "What do our best customers buy around our product that we could credibly own?",
    "Where are we competing on habit rather than on a deliberate choice?",
    "What evidence would make us abandon our current biggest bet — and do we collect it?",
    "Which segment finds us without marketing, and what does that tell us?",
    "What has changed in how our buyers discover, evaluate and purchase in the last two years?",
    "If we had to grow 30% with no new customers, whose wallet would it come from?",
    "What regulation, platform rule or input cost could rewrite our economics in 24 months?",
    "Which of our markets would we not enter if we were deciding today?",
    "What does the number two player in our category do better than us, specifically?",
  ],
  Customer: [
    "What is the exact moment a customer realises they need us — and who owns being present there?",
    "Which customer complaint have we normalised?",
    "What do customers say we do better than anyone — and have we verified it this year?",
    "Who is the customer we should politely refuse, and do we?",
    "What percentage of last year's customers came back — and does anyone review that monthly?",
    "What is the most common reason we lose a deal we wanted, evidenced not assumed?",
    "If our price rose 15% tomorrow, which customers stay instantly, and what does that prove?",
    "What do our customers' customers want that changes what our customers need from us?",
    "Which part of our offer do customers value most — and is it the part we talk about most?",
    "What would a customer say is the risk of choosing us, and how do we answer it before they ask?",
    "Who influences the buying decision that we never meet?",
    "What did our ten best customers have in common twelve months before they bought?",
    "If we disappeared next month, what would our customers genuinely miss?",
  ],
  Money: [
    "Where exactly does a rupee of revenue leak between list price and contribution?",
    "Which product, channel or customer would we discontinue if we ranked everything by contribution?",
    "What is our pocket price by channel — and who has seen that number this quarter?",
    "How much discount did we give last year that nobody consciously approved?",
    "What is the true cost of acquiring a customer, including everything, by channel?",
    "If revenue doubled with current systems, what breaks first: cash, supply, or people?",
    "Which fixed cost has never been challenged since it was first approved?",
    "What return did our biggest marketing investment actually produce, and how do we know?",
    "How many days of cash sit in receivables and inventory that a decision could release?",
    "What would we do differently if we measured success on contribution per customer, not revenue?",
    "Which revenue in the plan is assumed rather than evidenced?",
    "What is the cheapest source of our next crore — retention, price, or acquisition — and do we fund it accordingly?",
    "If we had half the budget, what would we protect — and why is it not clearly protected today?",
  ],
  Execution: [
    "Which decision took too long last quarter, and what would have made it fast?",
    "What are the three metrics this leadership team should never meet without reviewing?",
    "Who can say no to a discount — and does the organisation know?",
    "Which recurring meeting produces no decision, and why does it still exist?",
    "What capability must exist in-house in two years that we rent today?",
    "If our best salesperson left, what knowledge leaves with them?",
    "What did we decide at the last offsite that actually changed behaviour on Monday?",
    "Which role in the commercial team would we not refill if it became vacant?",
    "What do we measure that nobody acts on — and what do we act on that nobody measures?",
    "Where does a deal or order wait longest inside our own organisation?",
    "What would our frontline sales team say leadership misunderstands about the market?",
    "Which of our processes exists to protect us from a mistake we made once, years ago?",
    "If each of us could only keep three priorities this year, which three survive — and are they the same three?",
  ],
};

const ALL_CARDS = Object.entries(SUITS).flatMap(([suit, qs]) => qs.map((q, i) => ({ suit, q, n: i + 1 })));

export function ToolG11() {
  const [suit, setSuit] = useState("All");
  const [selected, setSelected] = useState([]);
  const [deck, setDeck] = useState(ALL_CARDS);
  const cards = useMemo(() => (suit === "All" ? deck : deck.filter((c) => c.suit === suit)), [deck, suit]);
  const shuffle = () => setDeck((d) => [...d].sort(() => Math.random() - 0.5));
  const toggle = (c) => setSelected((s) => (s.find((x) => x.q === c.q) ? s.filter((x) => x.q !== c.q) : [...s, c]));
  const [copied, setCopied] = useState(false);
  const exportList = async () => {
    const text = `Strategy Offsite — Discussion Pack\n\n${selected.map((c, i) => `${i + 1}. [${c.suit}] ${c.q}`).join("\n")}`;
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 2500);
    } catch {
      // Clipboard blocked (iframe/permissions) — selectable fallback.
      const ta = document.createElement("textarea");
      ta.value = text;
      ta.style.position = "fixed";
      ta.style.opacity = "0";
      document.body.appendChild(ta);
      ta.select();
      try { document.execCommand("copy"); setCopied(true); setTimeout(() => setCopied(false), 2500); } catch { /* user can select the list manually */ }
      document.body.removeChild(ta);
    }
  };
  return (
    <div>
      <div className="flex flex-wrap items-center gap-2" data-testid="g11-filters">
        {["All", ...Object.keys(SUITS)].map((s) => (
          <button key={s} type="button" onClick={() => setSuit(s)}
            className={`h-9 px-4 text-xs font-medium border transition-colors ${suit === s ? "bg-black text-white border-black" : "border-zinc-300 hover:border-black"}`}
            data-testid={`g11-suit-${s.toLowerCase()}`}>
            {s} {s !== "All" && `(${SUITS[s].length})`}
          </button>
        ))}
        <button type="button" onClick={shuffle} className="h-9 px-4 text-xs font-medium border border-zinc-300 hover:border-black" data-testid="g11-shuffle">Shuffle</button>
        <span className="text-xs text-zinc-500 ml-auto" data-testid="g11-count">{selected.length} selected</span>
      </div>
      <div className="mt-5 grid sm:grid-cols-2 gap-3" data-testid="g11-cards">
        {cards.map((c) => {
          const on = !!selected.find((x) => x.q === c.q);
          return (
            <button key={c.q} type="button" onClick={() => toggle(c)}
              className={`text-left border p-4 transition-colors ${on ? "border-black bg-zinc-50" : "border-zinc-200 bg-white hover:border-black"}`}
              data-testid={`g11-card-${c.suit.toLowerCase()}-${c.n}`}>
              <div className="flex items-center justify-between">
                <span className="text-[10px] font-semibold tracking-[0.2em] uppercase text-zinc-400">{c.suit} · {c.n}</span>
                <span className={`text-[10px] font-semibold ${on ? "text-black" : "text-zinc-300"}`}>{on ? "SELECTED" : "TAP TO SELECT"}</span>
              </div>
              <p className="mt-2 text-[15px] leading-snug text-zinc-900">{c.q}</p>
            </button>
          );
        })}
      </div>
      {selected.length > 0 && (
        <div className="mt-6 border-2 border-black bg-white p-6" data-testid="g11-pack">
          <div className="flex items-center justify-between">
            <div className="overline">Your discussion pack ({selected.length} questions)</div>
            <button type="button" onClick={exportList} className="text-sm font-medium underline underline-offset-4" data-testid="g11-copy">{copied ? "Copied" : "Copy pack"}</button>
          </div>
          <ol className="mt-3 space-y-1.5 text-sm text-zinc-700 list-decimal list-inside">
            {selected.map((c) => <li key={c.q}><span className="text-zinc-400">[{c.suit}]</span> {c.q}</li>)}
          </ol>
          <div className="mt-4"><Note>Suggested arc: 60 minutes = 6 questions (10 min each, decision noted per question); 90 = 9; 180 = 15 with a break. Every question ends with an owner and an action, or it was a conversation, not an offsite.</Note></div>
        </div>
      )}
    </div>
  );
}

/* ---------- P05 Pricing Power Assessment ---------- */
const P05_DIMS = [
  { id: "differentiation", label: "Objective differentiation", q: "Customers get something from us they measurably cannot get elsewhere" },
  { id: "switching", label: "Switching effort", q: "Changing supplier costs the customer real time, money or risk" },
  { id: "preference", label: "Preference evidence", q: "Customers choose us at equal or higher price — with proof, not anecdote" },
  { id: "transparency", label: "Price transparency", q: "Customers cannot instantly compare our price against five alternatives" },
  { id: "substitutes", label: "Substitute pressure", q: "Alternatives (including doing nothing) are weak or inconvenient" },
  { id: "channel", label: "Channel independence", q: "No single channel or partner controls our access to demand" },
  { id: "procurement", label: "Buyer power", q: "Purchases are not dominated by professional procurement squeezing price" },
  { id: "wtp", label: "Willingness-to-pay evidence", q: "We have tested (not assumed) that customers pay for the value we claim" },
];
const P05_SCALE = [{ v: 0, l: "No" }, { v: 1, l: "Weak" }, { v: 2, l: "Some" }, { v: 3, l: "Strong" }];

export function ToolP05() {
  const [answers, setAnswers] = useState({});
  const done = Object.keys(answers).length === P05_DIMS.length;
  const score = P05_DIMS.reduce((s, d) => s + (answers[d.id] ?? 0), 0);
  const max = P05_DIMS.length * 3;
  const power = Math.round((score / max) * 100);
  const weakest = [...P05_DIMS].sort((a, b) => (answers[a.id] ?? 9) - (answers[b.id] ?? 9)).slice(0, 3);
  return (
    <div>
      <div className="space-y-3">
        {P05_DIMS.map((d) => (
          <div key={d.id} className="border border-zinc-200 bg-white p-4 flex flex-wrap items-center justify-between gap-3">
            <div>
              <div className="text-sm font-medium">{d.label}</div>
              <div className="text-xs text-zinc-500 mt-0.5">{d.q}</div>
            </div>
            <div className="flex gap-1.5">
              {P05_SCALE.map((s) => (
                <button key={s.v} type="button" onClick={() => setAnswers((a) => ({ ...a, [d.id]: s.v }))}
                  className={`h-8 px-3 text-xs font-medium border ${answers[d.id] === s.v ? "bg-black text-white border-black" : "border-zinc-300 hover:border-black"}`}
                  data-testid={`p05-${d.id}-${s.v}`}>{s.l}</button>
              ))}
            </div>
          </div>
        ))}
      </div>
      {done && (
        <div className="mt-8 border border-zinc-200 bg-white p-6" data-testid="p05-result">
          <div className="overline">Pricing power profile</div>
          <div className="font-serif-display text-5xl mt-2">{power}<span className="text-2xl text-zinc-400">/100</span></div>
          <p className="mt-2 text-sm text-zinc-600">{power >= 70 ? "Strong evidence of pricing power. A careful increase is testable." : power >= 45 ? "Mixed. Pricing power exists in pockets — find and defend them." : "Weak evidence. Raising prices now is a gamble, not a strategy."}</p>
          <div className="mt-4">
            <div className="overline mb-2">Validate before you raise prices</div>
            {weakest.map((d) => (
              <div key={d.id} className="text-sm text-zinc-700 border border-black/10 px-3 py-2 mb-2">{d.label}: gather evidence — {d.id === "wtp" ? "run a price experiment with a control group" : d.id === "preference" ? "interview 10 recent wins on why they chose you" : "document current proof or create it"}</div>
            ))}
          </div>
          <div className="mt-4"><Note>Self-reported, deliberately conservative. Pricing power is what customers do, not what we believe — treat any score above 70 as a hypothesis to test, not a licence.</Note></div>
        </div>
      )}
    </div>
  );
}

/* ---------- P06 Pricing Experiment Planner ---------- */
export function ToolP06() {
  const [v, set] = useNumbers({
    hypothesis: "A 8% price increase on SKU-X loses less than 15% volume, raising contribution",
    metric: "Contribution per week", control: "2 comparable territories keep current price",
    guardrail: "Stop if volume falls >15% for 2 consecutive weeks",
    duration: "4 weeks", decision: "",
  });
  const fields = [
    ["Hypothesis (testable)", "hypothesis"], ["Success metric (one number)", "metric"],
    ["Control group / baseline", "control"], ["Guardrail (when you stop)", "guardrail"],
    ["Duration", "duration"], ["Decision rule after the test", "decision"],
  ];
  return (
    <div className="grid lg:grid-cols-2 gap-10">
      <div className="space-y-4">
        {fields.map(([label, k]) => (
          <label key={k} className="block">
            <span className="text-sm font-medium text-zinc-800">{label}</span>
            <textarea rows={2} className="mt-1.5 w-full px-3 py-2 text-sm border border-zinc-300 outline-none focus:border-black bg-white" value={v[k]} onChange={(e) => set(k)(e.target.value)} data-testid={`p06-${k}`} />
          </label>
        ))}
        <Note>Watch for interference: customers talk to each other, and festive weeks corrupt both legs of the test. B2B negotiated prices need account-level controls; online list prices need cohort or geo splits.</Note>
      </div>
      <div className="border-2 border-black bg-white p-6 h-fit" data-testid="p06-plan">
        <div className="overline">Experiment plan</div>
        <div className="mt-3 space-y-3 text-sm">
          {fields.map(([label, k]) => (
            <div key={k} className="border-b border-black/10 pb-2">
              <span className="text-[10px] font-semibold uppercase tracking-wider text-zinc-400">{label}</span>
              <p className="mt-0.5 text-zinc-800">{String(v[k]).trim() || <span className="italic text-zinc-300">Not defined.</span>}</p>
            </div>
          ))}
        </div>
        <p className="mt-4 text-xs text-zinc-500">Readout: compare metric per week, test vs control. Launch if the hypothesis holds AND guardrails stayed intact; iterate if mixed; reverse if guardrails broke. Sample sizes decide confidence — for meaningful volume tests, check with the sample-size calculator.</p>
      </div>
    </div>
  );
}

/* ---------- P07 Discount Approval & Deal Guardrail Matrix ---------- */
export function ToolP07() {
  const [v, set] = useNumbers({ price: 1000, variableCost: 700, minContributionPct: 15, salespersonMax: 3, managerMax: 8, headMax: 15 });
  const r = useMemo(() => {
    const price = Number(v.price) || 0, cost = Number(v.variableCost) || 0;
    const floor = cost / (1 - (Number(v.minContributionPct) || 0) / 100);
    const levels = [
      { role: "Salesperson", max: v.salespersonMax }, { role: "Sales manager", max: v.managerMax }, { role: "Commercial head", max: v.headMax },
    ].map((l) => {
      const dealPrice = price * (1 - (Number(l.max) || 0) / 100);
      const contribution = dealPrice - cost;
      return { ...l, dealPrice, contribution, allowed: dealPrice >= floor };
    });
    return { floor, levels, baseContribution: price - cost };
  }, [v]);
  return (
    <div className="grid lg:grid-cols-2 gap-10">
      <div className="space-y-5">
        <div className="grid grid-cols-2 gap-4">
          <Field label="List price" prefix="₹" value={v.price} onChange={set("price")} testid="p07-price" />
          <Field label="Variable cost" prefix="₹" value={v.variableCost} onChange={set("variableCost")} testid="p07-cost" />
        </div>
        <Field label="Minimum acceptable contribution" suffix="%" value={v.minContributionPct} onChange={set("minContributionPct")} testid="p07-floor" />
        <div className="grid grid-cols-3 gap-4">
          <Field label="Salesperson may give" suffix="%" value={v.salespersonMax} onChange={set("salespersonMax")} testid="p07-sales" />
          <Field label="Manager may give" suffix="%" value={v.managerMax} onChange={set("managerMax")} testid="p07-manager" />
          <Field label="Commercial head may give" suffix="%" value={v.headMax} onChange={set("headMax")} testid="p07-head" />
        </div>
        <Note>Guardrails govern contribution, not just percentage. Anything below the floor price needs a documented exception — volume commitments, payment terms, or strategic rationale in writing.</Note>
      </div>
      <div className="border border-zinc-200 bg-white p-6 h-fit">
        <div className="overline">The matrix</div>
        <div className="mt-3">
          <ResultRow label="Base contribution" value={`${inr(r.baseContribution)} / unit`} />
          <ResultRow strong label="Floor price (below this = never)" value={inr(r.floor)} tone="bad" />
          {r.levels.map((l) => (
            <ResultRow key={l.role} label={`${l.role} (up to ${l.max}% off → ${inr(l.dealPrice)})`} value={l.allowed ? `${inr(l.contribution)} contrib. ✓` : "Below floor — blocked"} tone={l.allowed ? undefined : "bad"} />
          ))}
        </div>
      </div>
    </div>
  );
}
